/* eslint @typescript-eslint/no-unused-vars: 0 */
import React, { Component } from 'react';
import { createStyles, StyledComponentProps, withStyles } from '@material-ui/core';
import { List } from 'immutable';
import { FormattedMessage } from 'react-intl';
import { Connect } from 'common/decorators';
import TableSection, {
  ITableHeaderColumn,
  ITableSectionColumn,
} from 'modules/reports/components/TableSection/TableSection';
import {
  selectInvoicesSummary,
  selectInvoicesSummaryLoading,
} from 'modules/reports/state/salesPOS/selectors';
import { fetchInvoicesSummary } from 'modules/reports/state/salesPOS/actions';
import { DEFAULT_TABLE_PARAMS } from 'common/constants/table';
import { IInvoicesSummaryItem, IInvoicesSummaryItemImt } from 'modules/reports/interfaces/salesPOS';
import { DispatchFunction } from 'common/state/interfaces';
import { ITableParams, Order } from 'common/interfaces/table';
import { formatPrice } from 'common/utils';
import messages from 'modules/reports/messages/messages';
import inputLabels from 'common/messages/inputLabels';
import { selectRoot } from 'common/hooks/useRootSelector';

const styles = () => createStyles({});

interface IInvoicesSummaryProps extends StyledComponentProps {
  mainTableParams: ITableParams;
  invoicesSummary?: List<IInvoicesSummaryItemImt>;
  invoicesSummaryLoading?: boolean;

  fetchInvoicesSummaryData?: (params?) => void;
}

@(withStyles(styles) as any)
@(Connect({
  mapStateToProps: () => ({
    invoicesSummary: selectRoot(selectInvoicesSummary()),
    invoicesSummaryLoading: selectRoot(selectInvoicesSummaryLoading()),
  }),
  mapDispatchToProps: (dispatch: DispatchFunction) => ({
    fetchInvoicesSummaryData: (requestOptions?) => dispatch(fetchInvoicesSummary(requestOptions)),
  }),
}) as any)
class InvoicesSummary extends Component<IInvoicesSummaryProps, ITableParams<IInvoicesSummaryItem>> {
  private readonly columnSettings: ITableSectionColumn[];

  private readonly headerSettings: ITableHeaderColumn<IInvoicesSummaryItem>[];

  constructor(props: IInvoicesSummaryProps) {
    super(props);

    this.state = {
      page: 0,
      perPage: 5,
      orderBy: 'amount',
      order: 'desc',
      filters: [],
    };

    this.columnSettings = [
      {
        name: 'type',
        label: <FormattedMessage {...inputLabels.type} />,
        transform: value => `${value[0]}${value?.slice(1).toLowerCase()}`,
      },
      {
        name: 'quantity',
        label: <FormattedMessage {...messages.qty} />,
        align: 'right',
      },
      {
        name: 'percent',
        label: '%',
        align: 'right',
      },
      {
        name: 'amount',
        label: <FormattedMessage {...inputLabels.amount} />,
        align: 'right',
        transform: value => formatPrice(value),
      },
    ];

    this.headerSettings = [
      {
        name: 'type',
        label: <FormattedMessage {...inputLabels.type} />,
        sort: true,
      },
      {
        name: 'quantity',
        label: <FormattedMessage {...messages.qty} />,
        sort: true,
        align: 'right',
      },
      {
        name: 'percent',
        label: '%',
        sort: true,
        align: 'right',
      },
      {
        name: 'amount',
        label: <FormattedMessage {...inputLabels.amount} />,
        align: 'right',
        sort: true,
      },
    ];
  }

  componentDidMount(): void {
    const { fetchInvoicesSummaryData, mainTableParams } = this.props;
    const { page, perPage, orderBy, order } = this.state;

    this.setState({ filters: mainTableParams.filters });

    if (fetchInvoicesSummaryData) {
      fetchInvoicesSummaryData({
        ...DEFAULT_TABLE_PARAMS,
        filters: mainTableParams.filters,
        page,
        perPage,
        orderBy,
        order,
      });
    }
  }

  componentDidUpdate(): void {
    const { mainTableParams, fetchInvoicesSummaryData } = this.props;
    const { page, perPage, orderBy, order, filters } = this.state;

    if (JSON.stringify(filters) !== JSON.stringify(mainTableParams.filters)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ filters: mainTableParams.filters });

      if (fetchInvoicesSummaryData) {
        fetchInvoicesSummaryData({
          ...DEFAULT_TABLE_PARAMS,
          filters: mainTableParams.filters,
          page,
          perPage,
          orderBy,
          order,
        });
      }
    }
  }

  handleChangeTableProps = (page: number, orderBy, order: Order): void => {
    this.updateTableParams({ ...this.state, page, orderBy, order });
  };

  updateTableParams = (tableParams: ITableParams<IInvoicesSummaryItem>): void => {
    this.setState(tableParams);
    const { fetchInvoicesSummaryData } = this.props;

    if (fetchInvoicesSummaryData) {
      fetchInvoicesSummaryData(tableParams);
    }
  };

  render(): JSX.Element {
    const { invoicesSummary, invoicesSummaryLoading } = this.props;
    const { page, perPage, orderBy, order, filters } = this.state;

    const params = new Array<string>();

    if (filters) {
      if (filters[0]) {
        if (filters[0].value && 'startDate' in filters[0].value)
          params.push(`periodStartDate=${Number(filters[0].value.startDate).toString()}`);

        if (filters[0].value && 'endDate' in filters[0].value)
          params.push(`periodEndDate=${Number(filters[0].value.endDate).toString()}`);
      }

      if (filters[1] && filters[1].value && filters[1].value instanceof Array)
        params.push(`${filters[1].name}=${filters[1].value.map(v => v.key).join(',')}`);

      if (filters[2] && filters[2].value && filters[2].value instanceof Array)
        params.push(`${filters[2].name}=${filters[2].value.map(v => v.key).join(',')}`);
    }

    return (
      <TableSection<IInvoicesSummaryItem, IInvoicesSummaryItemImt>
        id="invoices-summary-section"
        title={<FormattedMessage {...messages.invoicesSummaryTitle} />}
        columns={this.columnSettings}
        headerColumns={this.headerSettings}
        data={invoicesSummary as any} // TODO - PRM-3575 need types
        dataLoading={!!invoicesSummaryLoading}
        page={page}
        total={invoicesSummary?.size || 25}
        order={order}
        orderBy={orderBy}
        perPage={perPage}
        hidePagination
        onSettingsChanged={this.handleChangeTableProps}
        externalLink={`../invoice-history?${params.join('&')}`}
      />
    );
  }
}

export default InvoicesSummary;
